import React, { useState, useRef, useEffect, useMemo } from "react";
import { MagnifyingGlass, CaretUpDown, Check, X, Buildings, User } from "@phosphor-icons/react";

/**
 * SearchableContactSelect
 * 
 * Reusable searchable dropdown for Nusa CRM contacts.
 * Features:
 * - Real-time filtering by contact name and company
 * - Keyboard navigation (ArrowUp, ArrowDown, Enter, Escape)
 * - Click outside detection
 * - Clear button
 * - Clean mobile & desktop responsive layout
 * - Displays both Contact Name and Company Name clearly
 */
export default function SearchableContactSelect({
  contacts = [],
  value = "",
  onChange,
  placeholder = "Cari nama kontak atau perusahaan...",
  className = "",
  disabled = false,
  error = false,
  autoFillCompany = false,
  onCompanySelect,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Find currently selected contact
  const selectedContact = useMemo(() => {
    return contacts.find((c) => c.id === value) || null;
  }, [contacts, value]);

  // Filter contacts based on search query
  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const company = (c.company || "").toLowerCase();
      return name.includes(q) || company.includes(q);
    });
  }, [contacts, search]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch("");
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset highlight index when filtered list changes
  useEffect(() => {
    setHighlightedIndex(filteredContacts.length > 0 ? 0 : -1);
  }, [filteredContacts]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll("[data-contact-item]");
      const item = items[highlightedIndex];
      if (item) {
        item.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (contact) => {
    const newId = contact ? contact.id : "";
    onChange(newId);
    if (contact && autoFillCompany && onCompanySelect && contact.company) {
      onCompanySelect(contact.company);
    }
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange("");
    setSearch("");
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredContacts.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredContacts.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < filteredContacts.length) {
        handleSelect(filteredContacts[highlightedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearch("");
      setHighlightedIndex(-1);
    }
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
      data-testid="searchable-contact-select"
    >
      {/* Trigger Button / Display Box */}
      <div
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={!disabled ? handleKeyDown : undefined}
        className={`w-full rounded-md border bg-white px-3 py-2 text-sm text-[#173042] flex items-center justify-between cursor-pointer transition-colors shadow-2xs select-none ${
          disabled
            ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
            : error
            ? "border-red-400 focus:border-red-500 focus:ring-1 focus:ring-red-400"
            : isOpen
            ? "border-[#173B4D] ring-1 ring-[#173B4D]"
            : "border-[#CBD5E1] hover:border-[#94A3B8] focus:border-[#173B4D] focus:ring-1 focus:ring-[#173B4D]"
        }`}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1 pr-1">
          {selectedContact ? (
            <div className="flex items-center gap-1.5 truncate">
              <span className="font-semibold text-[#173042] truncate">
                {selectedContact.name}
              </span>
              {selectedContact.company && (
                <span className="text-xs text-[#64748B] truncate">
                  ({selectedContact.company})
                </span>
              )}
            </div>
          ) : (
            <span className="text-slate-400 truncate text-sm">
              -- Pilih Kontak --
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {selectedContact && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              title="Hapus pilihan"
              className="p-0.5 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
            >
              <X size={14} weight="bold" />
            </button>
          )}
          <CaretUpDown size={15} weight="bold" className="text-slate-400" />
        </div>
      </div>

      {/* Floating Search & Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 bg-white rounded-lg border border-[#CBD5E1] shadow-xl overflow-hidden animate-in fade-in-50 zoom-in-95 duration-100 max-h-72 flex flex-col"
          style={{ minWidth: "260px" }}
        >
          {/* Search Input Box */}
          <div className="p-2 border-b border-[#E2E8F0] bg-[#F8FAFC] flex items-center gap-2">
            <MagnifyingGlass size={16} weight="bold" className="text-[#64748B] shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="w-full bg-transparent text-xs text-[#173042] placeholder:text-slate-400 focus:outline-none"
              data-testid="contact-search-input"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="text-slate-400 hover:text-slate-600 p-0.5"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>

          {/* Results List */}
          <div
            ref={listRef}
            role="listbox"
            className="overflow-y-auto max-h-56 divide-y divide-slate-100 p-1"
          >
            {/* Clear option if contact is selected */}
            {selectedContact && !search && (
              <div
                role="option"
                aria-selected={false}
                onClick={() => handleSelect(null)}
                className="px-2.5 py-1.5 text-xs text-slate-500 hover:bg-slate-100 rounded cursor-pointer transition-colors flex items-center justify-between"
              >
                <span>-- Kosongkan Pilihan Kontak --</span>
              </div>
            )}

            {filteredContacts.length > 0 ? (
              filteredContacts.map((c, idx) => {
                const isSelected = c.id === value;
                const isHighlighted = idx === highlightedIndex;

                return (
                  <div
                    key={c.id}
                    data-contact-item
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(c)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-3 py-2 text-xs rounded-md cursor-pointer transition-colors flex items-center justify-between gap-2 ${
                      isSelected
                        ? "bg-[#E6F3F5] text-[#0F7180] font-semibold"
                        : isHighlighted
                        ? "bg-[#F1F5F9] text-[#173042]"
                        : "text-[#173042] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 truncate">
                        <User size={13} weight={isSelected ? "bold" : "regular"} className="shrink-0 text-slate-400" />
                        <span className="truncate font-medium">{c.name}</span>
                      </div>
                      {c.company && (
                        <div className="flex items-center gap-1.5 truncate text-[11px] text-slate-500 mt-0.5">
                          <Buildings size={12} className="shrink-0 text-slate-400" />
                          <span className="truncate">{c.company}</span>
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <Check size={14} weight="bold" className="text-[#0F7180] shrink-0" />
                    )}
                  </div>
                );
              })
            ) : (
              <div className="py-6 px-3 text-center text-xs text-slate-400">
                <p className="font-medium text-slate-500">Kontak tidak ditemukan</p>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Tidak ada kontak yang cocok dengan &quot;{search}&quot;
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
